import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Inbox,
  Mail,
  RefreshCw,
  Server,
  ShieldAlert,
  XCircle,
} from 'lucide-react';

const CLASSIFICATION_LABELS = {
  ip_policy_block: 'IP-/Reputationsblock',
  content_policy: 'Content-/Policy-Ablehnung',
  invalid_recipient: 'Empfänger ungültig',
  mailbox_full: 'Postfach voll',
  temporary: 'Temporärer Fehler',
  unknown: 'Unbekannte Ursache',
};

const SCAN_STATUS = {
  pending: { label: 'Wartet', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
  running: { label: 'Prüft', className: 'bg-blue-100 text-blue-800', icon: RefreshCw },
  completed: { label: 'Aktuell', className: 'bg-green-100 text-green-800', icon: CheckCircle },
  partial: { label: 'Teilweise', className: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  failed: { label: 'Fehlgeschlagen', className: 'bg-red-100 text-red-800', icon: XCircle },
};

const ACCOUNT_STATUS = {
  pending: 'Wartet',
  running: 'Prüft',
  ok: 'OK',
  partial: 'Teilweise',
  error: 'Fehler',
  proxy_error: 'Proxy-Fehler',
  needs_reauth: 'Neu autorisieren',
  unsupported: 'Nicht unterstützt',
};

const PLACEMENT_STATUS = {
  pending: { label: 'Wartet', className: 'bg-yellow-100 text-yellow-800' },
  running: { label: 'Prüft', className: 'bg-blue-100 text-blue-800' },
  ok: { label: 'Aktuell', className: 'bg-green-100 text-green-800' },
  partial: { label: 'Teilweise', className: 'bg-orange-100 text-orange-800' },
  error: { label: 'Fehler', className: 'bg-red-100 text-red-800' },
  proxy_error: { label: 'Proxy-Fehler', className: 'bg-red-100 text-red-800' },
  auth_error: { label: 'Login-Fehler', className: 'bg-red-100 text-red-800' },
};

const number = value => Number(value || 0).toLocaleString('de-DE');

const formatDate = value => {
  if (!value) return '–';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '–'
    : date.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'medium' });
};

const Metric = ({ label, value, detail, color = 'text-gray-900' }) => (
  <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-4">
    <div className={`text-2xl font-bold ${color}`}>{value}</div>
    <div className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-200">{label}</div>
    {detail && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{detail}</div>}
  </div>
);

const StatusBadge = ({ status }) => {
  const config = SCAN_STATUS[status] || {
    label: status || 'Nicht geprüft',
    className: 'bg-gray-100 text-gray-700',
    icon: Clock,
  };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${config.className}`}>
      <Icon className={`mr-1.5 h-3.5 w-3.5 ${status === 'running' ? 'animate-spin' : ''}`} />
      {config.label}
    </span>
  );
};

const PlacementStatusBadge = ({ status }) => {
  const config = PLACEMENT_STATUS[status] || {
    label: status || 'Nicht gestartet',
    className: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

const GroupChips = ({ title, rows, label }) => {
  if (!rows?.length) return null;
  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.map(row => (
          <span
            key={`${title}-${row.value}`}
            className="rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1 text-sm text-gray-700 dark:text-gray-100"
          >
            <strong>{number(row.count)}×</strong> {label ? label(row.value) : row.value || 'unbekannt'}
          </span>
        ))}
      </div>
    </div>
  );
};

const NdrBounceOverview = ({ campaignId, campaignStatus }) => {
  const queryClient = useQueryClient();
  const [showDetails, setShowDetails] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);

  const summaryQuery = useQuery(
    ['campaignNdrSummary', campaignId],
    async () => {
      const response = await axios.get(`/api/multi-campaigns/${campaignId}/ndr-summary`);
      return response.data.data;
    },
    {
      refetchInterval: data => {
        const scanStatus = data?.scan?.latest?.status;
        const placementActive = data?.placement?.targets?.some(
          target => ['pending', 'running'].includes(target.status)
        );
        if (
          campaignStatus === 'running'
          || ['pending', 'running'].includes(scanStatus)
          || placementActive
        ) return 10000;
        return false;
      },
      refetchIntervalInBackground: true,
      retry: 1,
    }
  );

  const eventsQuery = useQuery(
    ['campaignNdrEvents', campaignId, eventsPage],
    async () => {
      const response = await axios.get(`/api/multi-campaigns/${campaignId}/ndr-events`, {
        params: { page: eventsPage, limit: 50 },
      });
      return response.data.data;
    },
    {
      enabled: showDetails,
      keepPreviousData: true,
      refetchInterval: summaryQuery.data?.scan?.latest?.status === 'running' ? 10000 : false,
    }
  );

  const scanMutation = useMutation(
    async () => {
      const response = await axios.post(`/api/multi-campaigns/${campaignId}/ndr-scan`, {
        historical: campaignStatus === 'completed' && !summaryQuery.data?.scan?.latest,
      });
      return response.data.data;
    },
    {
      onSuccess: data => {
        toast.success(data?.alreadyActive ? 'Prüfung läuft bereits' : 'NDR-/Placement-Prüfung gestartet');
        queryClient.invalidateQueries(['campaignNdrSummary', campaignId]);
      },
      onError: error => {
        toast.error(error?.response?.data?.error || 'NDR-Prüfung konnte nicht gestartet werden');
      },
    }
  );

  const summary = summaryQuery.data;
  const latest = summary?.scan?.latest;
  const accounts = useMemo(() => summary?.scan?.accounts || [], [summary]);
  const accountCounts = useMemo(() => accounts.reduce((counts, account) => {
    counts[account.status] = (counts[account.status] || 0) + 1;
    return counts;
  }, {}), [accounts]);

  if (summaryQuery.isLoading) {
    return (
      <div className="mt-6 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 p-8">
        <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
        <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">NDR-Statistik wird geladen…</span>
      </div>
    );
  }

  if (summaryQuery.isError) {
    return (
      <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        NDR-Statistik konnte nicht geladen werden.
        <button className="ml-2 underline" onClick={() => summaryQuery.refetch()}>Erneut versuchen</button>
      </div>
    );
  }

  const topCode = summary?.groups?.smtp_codes?.[0];
  const topClassification = summary?.groups?.classifications?.[0];
  const causeHeadline = topCode && topClassification
    ? `${number(Math.min(topCode.count, topClassification.count))} × ${topCode.value} / ${CLASSIFICATION_LABELS[topClassification.value] || topClassification.value}`
    : null;
  const totalPages = Math.max(1, Math.ceil(Number(eventsQuery.data?.total || 0) / 50));

  return (
    <section className="mt-6 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700">
      <div className="border-b border-gray-200 dark:border-gray-600 p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-orange-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {summary?.placement?.eligible ? 'NDR- & Zustellforensik' : 'NDR- & Bounce-Forensik'}
              </h2>
              <StatusBadge status={latest?.status} />
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
              {summary?.placement?.eligible
                ? 'SMTP-Annahmen, nachträgliche Zustellfehler und Testpostfach-Placement werden getrennt ausgewiesen.'
                : 'SMTP-Annahmen und nachträgliche Zustellfehler werden getrennt ausgewiesen.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => scanMutation.mutate()}
            disabled={scanMutation.isLoading || ['pending', 'running'].includes(latest?.status)}
            className="inline-flex items-center justify-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${scanMutation.isLoading || latest?.status === 'running' ? 'animate-spin' : ''}`} />
            Jetzt prüfen
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Metric label="SMTP akzeptiert" value={number(summary?.accepted)} color="text-green-600" />
          <Metric label="Nachträgliche NDRs" value={number(summary?.known_ndr)} color="text-red-600" />
          <Metric label="Hard Bounces" value={number(summary?.hard_bounces)} detail="5xx / permanent" color="text-red-700" />
          <Metric label="Soft Bounces" value={number(summary?.soft_bounces)} detail="4xx / temporär" color="text-yellow-600" />
          <Metric
            label="Ohne bekannten NDR"
            value={number(summary?.accepted_without_known_ndr)}
            detail="Keine Zustellgarantie"
            color="text-blue-600"
          />
          <Metric label="NDR-Rate" value={`${Number(summary?.ndr_rate || 0).toFixed(2)} %`} color="text-orange-600" />
        </div>

        {causeHeadline && (
          <div className="mt-4 flex items-start rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
            <AlertTriangle className="mr-2 mt-0.5 h-5 w-5 flex-none" />
            <div>
              <div className="font-semibold">{causeHeadline}</div>
              <div className="text-xs text-red-700">Häufigste erkannte Bounce-Ursache dieser Campaign</div>
            </div>
          </div>
        )}

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <GroupChips
            title="Ursachen"
            rows={summary?.groups?.classifications}
            label={value => CLASSIFICATION_LABELS[value] || value}
          />
          <GroupChips title="SMTP-Codes" rows={summary?.groups?.smtp_codes} />
          <GroupChips title="Gemeldete Ausgangs-IPs" rows={summary?.groups?.source_ips} />
          <GroupChips title="Senderdomains" rows={summary?.groups?.sender_domains} />
        </div>

        {summary?.placement?.eligible && (
          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="flex items-center text-base font-semibold text-gray-900 dark:text-white">
                  <Inbox className="mr-2 h-5 w-5 text-blue-600" />
                  Testpostfach-Zustellung
                </h3>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
                  Exakter Message-ID-Abgleich in INBOX und Spam bei Leo/Konren; NDR hat Vorrang.
                </p>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {summary.placement.finalized ? 'Final geprüft (+30 Min.)' : 'Nachlauf aktiv'}
              </span>
            </div>

            {!summary.placement.monitored ? (
              <div className="mt-4 rounded-md border border-blue-200 bg-white p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-gray-800 dark:text-blue-200">
                Diese historische Campaign wurde nicht automatisch nachgescannt. Neue passende Campaigns werden beim Versand automatisch überwacht.
              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                  <Metric
                    label="Getrackt"
                    value={number(summary.placement.tracked)}
                    detail={`${number(summary.placement.accepted)} akzeptiert`}
                    color="text-gray-900 dark:text-white"
                  />
                  <Metric label="INBOX" value={number(summary.placement.inbox)} color="text-green-600" />
                  <Metric label="Spam" value={number(summary.placement.spam)} color="text-orange-600" />
                  <Metric label="NDR" value={number(summary.placement.ndr)} color="text-red-600" />
                  <Metric label="Ausstehend" value={number(summary.placement.pending)} color="text-blue-600" />
                  <Metric
                    label="Nicht gefunden"
                    value={number(summary.placement.not_found)}
                    detail={summary.placement.finalized ? 'Nach finalem Check' : 'Noch nicht final'}
                    color="text-purple-700"
                  />
                </div>

                {Number(summary.placement.untracked || 0) > 0 && (
                  <div className="mt-3 flex items-start text-xs text-orange-700 dark:text-orange-300">
                    <AlertTriangle className="mr-1.5 mt-0.5 h-4 w-4 flex-none" />
                    {number(summary.placement.untracked)} akzeptierte Mails besitzen noch kein Placement-Tracking.
                  </div>
                )}

                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  {(summary.placement.targets || []).map(target => (
                    <div
                      key={target.recipient_email}
                      className="rounded-md border border-blue-100 bg-white p-3 dark:border-blue-900 dark:bg-gray-800"
                      title={target.last_error || ''}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {target.recipient_email}
                        </span>
                        <PlacementStatusBadge
                          status={Number(target.untracked || 0) > 0 ? 'pending' : target.status}
                        />
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                        {number(target.inbox)} INBOX · {number(target.spam)} Spam · {number(target.ndr)} NDR · {number(target.pending)} offen · {number(target.not_found)} nicht gefunden
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Letzter Check: {formatDate(target.last_scan_at)}
                      </div>
                      {target.last_error && (
                        <div className="mt-1 text-xs text-red-600 dark:text-red-300">{target.last_error}</div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-5 grid gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-center text-sm text-gray-700 dark:text-gray-200">
            <Server className="mr-2 h-4 w-4 text-gray-500" />
            {number(accounts.length)} Senderkonten
          </div>
          <div className="flex items-center text-sm text-gray-700 dark:text-gray-200">
            <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
            {number(accountCounts.ok)} erfolgreich geprüft
          </div>
          <div className="flex items-center text-sm text-gray-700 dark:text-gray-200">
            <XCircle className="mr-2 h-4 w-4 text-red-600" />
            {number((accountCounts.error || 0) + (accountCounts.proxy_error || 0) + (accountCounts.needs_reauth || 0))} mit Fehler
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Letzter Lauf: {formatDate(latest?.completed_at || latest?.started_at)}
          </div>
        </div>

        {Number(summary?.unmatched_ndr || 0) > 0 && (
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Zusätzlich erkannt, aber nicht eindeutig dieser Campaign zugeordnet: {number(summary.unmatched_ndr)} NDRs.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={() => setShowDetails(value => !value)}
        className="flex w-full items-center justify-between px-5 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
      >
        <span>Konten und einzelne NDRs anzeigen</span>
        {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {showDetails && (
        <div className="space-y-6 border-t border-gray-200 dark:border-gray-600 p-5">
          <div>
            <h3 className="mb-3 flex items-center text-sm font-semibold text-gray-900 dark:text-white">
              <Server className="mr-2 h-4 w-4" /> Senderkonten
            </h3>
            <div className="max-h-80 overflow-auto rounded-lg border border-gray-200 dark:border-gray-600">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-600">
                <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left">Konto</th>
                    <th className="px-3 py-2 text-left">Quelle</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">NDRs</th>
                    <th className="px-3 py-2 text-left">Letzter Scan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {accounts.map(account => (
                    <tr key={account.smtp_server_id} title={account.last_error || ''}>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">{account.email}</td>
                      <td className="px-3 py-2 uppercase text-gray-500 dark:text-gray-300">{account.source}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                        {ACCOUNT_STATUS[account.status] || account.status}
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        {number(account.total_event_count)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-300">
                        {formatDate(account.last_scan_at)}
                      </td>
                    </tr>
                  ))}
                  {accounts.length === 0 && (
                    <tr><td colSpan="5" className="px-3 py-5 text-center text-gray-500">Noch keine Konten registriert.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 className="mb-3 flex items-center text-sm font-semibold text-gray-900 dark:text-white">
              <Mail className="mr-2 h-4 w-4" /> Erkannte NDRs
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-600">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-3 py-2 text-left">Zeit</th>
                    <th className="px-3 py-2 text-left">Sender</th>
                    <th className="px-3 py-2 text-left">Empfänger</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Ursache / IP</th>
                    <th className="px-3 py-2 text-left">Zuordnung</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {(eventsQuery.data?.items || []).map(event => (
                    <tr key={event.id}>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-500 dark:text-gray-300">
                        {formatDate(event.ndr_received_at || event.detected_at)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">{event.sender_email || '–'}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-gray-900 dark:text-gray-100">{event.recipient_email || '–'}</td>
                      <td className="px-3 py-2 font-semibold text-red-600">
                        {event.smtp_code || event.enhanced_status || '–'}
                      </td>
                      <td className="max-w-md px-3 py-2 text-gray-700 dark:text-gray-200" title={event.diagnostic || ''}>
                        <div>{CLASSIFICATION_LABELS[event.classification] || event.classification}</div>
                        {event.source_ip && <div className="font-mono text-xs text-gray-500">{event.source_ip}</div>}
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-300">{event.correlation_method}</td>
                    </tr>
                  ))}
                  {eventsQuery.isError && (
                    <tr>
                      <td colSpan="6" className="px-3 py-5 text-center text-red-600">
                        NDR-Details konnten nicht geladen werden.
                        <button className="ml-2 underline" onClick={() => eventsQuery.refetch()}>
                          Erneut versuchen
                        </button>
                      </td>
                    </tr>
                  )}
                  {!eventsQuery.isLoading && !eventsQuery.isError && (eventsQuery.data?.items || []).length === 0 && (
                    <tr><td colSpan="6" className="px-3 py-5 text-center text-gray-500">Keine NDRs gefunden.</td></tr>
                  )}
                  {eventsQuery.isLoading && (
                    <tr><td colSpan="6" className="px-3 py-5 text-center text-gray-500">NDRs werden geladen…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-300">
              <span>{number(eventsQuery.data?.total)} Ereignisse</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={eventsPage <= 1}
                  onClick={() => setEventsPage(page => Math.max(1, page - 1))}
                  className="rounded border border-gray-300 p-1 disabled:opacity-40 dark:border-gray-600"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span>Seite {eventsPage} / {totalPages}</span>
                <button
                  type="button"
                  disabled={eventsPage >= totalPages}
                  onClick={() => setEventsPage(page => page + 1)}
                  className="rounded border border-gray-300 p-1 disabled:opacity-40 dark:border-gray-600"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default NdrBounceOverview;
